import { getGetAnalyticsQueryKey } from '@reelplicate/api-client/analytics';
import {
  getListSocialAccountsQueryKey,
  useCheckSocialAccount,
} from '@reelplicate/api-client/social-accounts';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';

/**
 * "Check again": read an account's public numbers now, whatever the last
 * read said, then refresh the account lists and Analytics and say how it went.
 */
export function useCheckAccount(workspaceId: string) {
  const queryClient = useQueryClient();
  const check = useCheckSocialAccount();

  const run = async (account: { id: string; handle: string }) => {
    let result;
    try {
      result = await check.mutateAsync({ id: workspaceId, accountId: account.id });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListSocialAccountsQueryKey(workspaceId),
      }),
      queryClient.invalidateQueries({ queryKey: getGetAnalyticsQueryKey(workspaceId) }),
    ]);
    if (result.statsStatus === 'ACTIVE') {
      toast.success(`${account.handle} found - numbers are in`);
    } else if (result.statsStatus === 'NOT_FOUND') {
      toast.warning(`${account.handle} wasn’t found on the platform`);
    } else if (result.statsStatus === 'ERROR') {
      toast.error(result.statsError ?? `${account.handle} couldn’t be read`);
    } else {
      toast(
        `${account.handle} couldn’t be read just now - it’s retried every hour${result.statsError ? `: ${result.statsError}` : ''}`,
      );
    }
  };

  return { run, pending: check.isPending, checking: check.variables?.accountId };
}
